import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Cart action types
const CART_ACTIONS = {
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_QUANTITY: 'UPDATE_QUANTITY',
    CLEAR_CART: 'CLEAR_CART',
    LOAD_CART: 'LOAD_CART',
    HYDRATE_PRICES: 'HYDRATE_PRICES'
};

// Initial state
const initialState = {
    items: [],
    isLoaded: false
};

// Cart reducer
function cartReducer(state, action) {
    switch (action.type) {
        case CART_ACTIONS.ADD_ITEM: {
            const existingIndex = state.items.findIndex(
                item => item.id === action.payload.id
            );

            if (existingIndex >= 0) {
                // Update quantity if item exists
                const updatedItems = [...state.items];
                updatedItems[existingIndex] = {
                    ...updatedItems[existingIndex],
                    quantity: updatedItems[existingIndex].quantity + (action.payload.quantity || 1)
                };
                return { ...state, items: updatedItems };
            }

            // Add new item
            return {
                ...state,
                items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }]
            };
        }

        case CART_ACTIONS.REMOVE_ITEM: {
            return {
                ...state,
                items: state.items.filter(item => item.id !== action.payload.id)
            };
        }

        case CART_ACTIONS.UPDATE_QUANTITY: {
            const { id, quantity } = action.payload;

            if (quantity <= 0) {
                return {
                    ...state,
                    items: state.items.filter(item => item.id !== id)
                };
            }

            return {
                ...state,
                items: state.items.map(item =>
                    item.id === id ? { ...item, quantity } : item
                )
            };
        }

        case CART_ACTIONS.CLEAR_CART: {
            return { ...state, items: [] };
        }

        case CART_ACTIONS.LOAD_CART: {
            return { ...state, items: action.payload, isLoaded: true };
        }

        case CART_ACTIONS.HYDRATE_PRICES: {
            // Merge live price/availability (keyed by SKU = item.id) into cart items.
            // Only overwrite when a real live price exists; leave unpriced items as-is
            // (they stay "Contact for Price" / quote-only, matching checkout).
            const priceMap = action.payload || {};
            let changed = false;
            const updatedItems = state.items.map(item => {
                const info = priceMap[item.id];
                const liveNumeric = info ? Number(info.priceNumeric) : 0;
                if (info && liveNumeric > 0) {
                    const rounded = Math.round(liveNumeric * 100) / 100;
                    if (item.priceNumeric === rounded && item.price === `$${rounded.toFixed(2)}`) {
                        return item; // already up to date
                    }
                    changed = true;
                    return {
                        ...item,
                        price: `$${rounded.toFixed(2)}`,
                        priceNumeric: rounded,
                        inStock: info.inStock !== undefined ? info.inStock : item.inStock,
                        availableQuantity: info.availableQuantity !== undefined ? info.availableQuantity : item.availableQuantity
                    };
                }
                return item;
            });
            return changed ? { ...state, items: updatedItems } : state;
        }

        default:
            return state;
    }
}

// Create context
const CartContext = createContext(null);

// Local storage key
const CART_STORAGE_KEY = 'jaak_cart';

// Cart Provider component
export function CartProvider({ children }) {
    const [state, dispatch] = useReducer(cartReducer, initialState);

    // Load cart from localStorage on mount
    useEffect(() => {
        try {
            const savedCart = localStorage.getItem(CART_STORAGE_KEY);
            if (savedCart) {
                const parsedCart = JSON.parse(savedCart);
                dispatch({ type: CART_ACTIONS.LOAD_CART, payload: parsedCart });
            } else {
                dispatch({ type: CART_ACTIONS.LOAD_CART, payload: [] });
            }
        } catch (error) {
            console.error('Failed to load cart from localStorage:', error);
            dispatch({ type: CART_ACTIONS.LOAD_CART, payload: [] });
        }
    }, []);

    // Save cart to localStorage whenever items change
    useEffect(() => {
        if (state.isLoaded) {
            try {
                localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items));
            } catch (error) {
                console.error('Failed to save cart to localStorage:', error);
            }
        }
    }, [state.items, state.isLoaded]);

    // Cart actions
    const addItem = (product, quantity = 1) => {
        dispatch({
            type: CART_ACTIONS.ADD_ITEM,
            payload: { ...product, quantity }
        });
    };

    const removeItem = (productId) => {
        dispatch({
            type: CART_ACTIONS.REMOVE_ITEM,
            payload: { id: productId }
        });
    };

    const updateQuantity = (productId, quantity) => {
        dispatch({
            type: CART_ACTIONS.UPDATE_QUANTITY,
            payload: { id: productId, quantity }
        });
    };

    const clearCart = () => {
        dispatch({ type: CART_ACTIONS.CLEAR_CART });
    };

    // Merge freshly-fetched live prices (SKU-keyed map) into the cart items
    const hydratePrices = (priceMap) => {
        if (!priceMap || Object.keys(priceMap).length === 0) return;
        dispatch({ type: CART_ACTIONS.HYDRATE_PRICES, payload: priceMap });
    };

    const isInCart = (productId) => {
        return state.items.some(item => item.id === productId);
    };

    const getItemQuantity = (productId) => {
        const item = state.items.find(item => item.id === productId);
        return item ? item.quantity : 0;
    };

    // Computed values
    // Number of distinct products in the cart (matches the "X items" shown elsewhere)
    const itemCount = state.items.length;

    const subtotal = state.items.reduce((total, item) => {
        const price = item.priceNumeric || parseFloat(item.price?.replace(/[^0-9.]/g, '')) || 0;
        return total + (price * item.quantity);
    }, 0);

    const formattedSubtotal = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(subtotal);

    const value = {
        items: state.items,
        itemCount,
        subtotal,
        formattedSubtotal,
        isLoaded: state.isLoaded,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        hydratePrices,
        isInCart,
        getItemQuantity
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}

// Custom hook to use cart context
export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}

export default CartContext;
